/**
 * Captura o hash IMEDIATAMENTE ao carregar a página,
 * ANTES do React Router ou qualquer outro processamento limpar.
 * 
 * CRÍTICO: Este arquivo deve ser importado o mais cedo possível!
 */

// Import dinâmico para evitar dependência circular
let loggerModule: typeof import('./lib/terminalLogger') | null = null;
const getLogger = () => {
  if (!loggerModule) {
    try {
      loggerModule = require('./lib/terminalLogger');
    } catch {
      return null;
    }
  }
  return loggerModule?.logger;
};

// Capturar hash IMEDIATAMENTE (executa no nível do módulo)
const initialHash = window.location.hash || '';
const initialSearch = window.location.search || '';
const initialHref = window.location.href || '';

// Salvar em variáveis globais para acesso posterior
(window as any).__INITIAL_HASH__ = initialHash;
(window as any).__INITIAL_SEARCH__ = initialSearch;
(window as any).__INITIAL_HREF__ = initialHref;

// Log para debug
try {
  const logger = getLogger();
  if (logger) {
    logger.debug('[InitialHash] Capturado:', {
      hash: initialHash.substring(0, 50) + '...',
      search: initialSearch,
      href: initialHref.substring(0, 100) + '...',
      hasToken: initialHash.includes('token=') || initialSearch.includes('token=')
    });
  } else {
    console.log('[InitialHash] Capturado:', {
      hash: initialHash.substring(0, 50) + '...',
      search: initialSearch,
      href: initialHref.substring(0, 100) + '...',
      hasToken: initialHash.includes('token=') || initialSearch.includes('token=')
    });
  }
} catch {}

/**
 * Obter hash inicial capturado ANTES do React processar
 */
export function getInitialHash(): string {
  return (window as any).__INITIAL_HASH__ || '';
}

/**
 * Obter search inicial capturado ANTES do React processar
 */
export function getInitialSearch(): string {
  return (window as any).__INITIAL_SEARCH__ || '';
}

/**
 * Obter href inicial capturado ANTES do React processar
 */
export function getInitialHref(): string {
  return (window as any).__INITIAL_HREF__ || '';
}

function getHashRouteParts(hash: string): { routePath: string | null; routeQuery: string } {
  if (!hash || !hash.startsWith('#/')) {
    return { routePath: null, routeQuery: '' };
  }

  const raw = hash.slice(1);
  const [routePath, routeQuery = ''] = raw.split('?', 2);
  return {
    routePath: routePath || null,
    routeQuery
  };
}

export function describeInitialTokenSource(
  source: 'hash' | 'search' | null
): {
  source: 'hash' | 'search' | null;
  channel: 'webview-bootstrap' | 'none';
  isSocketIo: boolean;
} {
  if (!source) {
    return {
      source: null,
      channel: 'none',
      isSocketIo: false,
    };
  }

  return {
    source,
    channel: 'webview-bootstrap',
    isSocketIo: false,
  };
}

export const __initialTokenDiagnostics = {
  describeSource: describeInitialTokenSource,
};

/**
 * Extrair token do hash/search inicial
 */
export function extractInitialToken(): { token: string | null; ud: string | null; source: 'hash' | 'search' | null } {
  const hash = getInitialHash();
  const search = getInitialSearch();
  
  // Tentar hash primeiro
  if (hash) {
    const hashParams = new URLSearchParams(hash.replace(/^#/, ''));
    const token = hashParams.get('token');
    const ud = hashParams.get('ud');
    if (token) {
      return { token, ud, source: 'hash' };
    }

    // Compatibilidade com URLs no formato "#/embedded/tools?token=...&ud=..."
    const { routeQuery } = getHashRouteParts(hash);
    if (routeQuery) {
      const routeParams = new URLSearchParams(routeQuery);
      const routeToken = routeParams.get('token');
      const routeUd = routeParams.get('ud');
      if (routeToken) {
        return { token: routeToken, ud: routeUd, source: 'hash' };
      }
    }
  }
  
  // Fallback: tentar search
  if (search) {
    const searchParams = new URLSearchParams(search);
    const token = searchParams.get('token');
    const ud = searchParams.get('ud');
    if (token) {
      return { token, ud, source: 'search' };
    }
  }
  
  return { token: null, ud: null, source: null };
}

export function getInitialHashRoutePath(): string | null {
  const { routePath } = getHashRouteParts(getInitialHash());
  return routePath;
}
